import { useEffect, useRef, useState } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { useForm } from "react-hook-form";

import { api_without_cancellation } from "../../../../api/api";
import { formatDateForPanelDisplay } from "../../../../Utils/helper";
import addIconGold from "../../../../assets/Add-icon-gold.svg";

const COLS = 3;
const MIN_ROWS = 15;
const HISTORY_ENDPOINT = "/api/employment/history/";

const eachDay = (from, to) => {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to || from}T00:00:00Z`);
  if (isNaN(start) || isNaN(end) || end < start) return from ? [from] : [];
  const out = [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
};

// Per-day expansion — PREVIEW ONLY. The backend is posted ranges, not days.
export const expandEmploymentDays = ({ employerId, employerName, start, end, missed = [] }) => {
  const rows = [];
  if (start) rows.push({ employerId, employerName, date: start, label: "First Day" });
  if (end) rows.push({ employerId, employerName, date: end, label: "Last Day" });
  missed.forEach(({ from, to }) => {
    if (!from) return;
    eachDay(from, to).forEach((date) =>
      rows.push({ employerId, employerName, date, label: "Day work missed" })
    );
  });
  const seen = new Set();
  return rows
    .filter((r) => (seen.has(r.date) ? false : (seen.add(r.date), true)))
    .sort((a, b) => a.date.localeCompare(b.date));
};

// Serialize accumulated entries into the backend's range rows.
const toHistoryPayload = (entries) =>
  entries.flatMap((e) => {
    const rows = [];
    if (e.start || e.end) {
      rows.push({
        employment_id: e.employerId,
        start_date: e.start || null,
        end_date: e.end || null,
        name: "employment",
      });
    }
    (e.missed || []).forEach((m) => {
      if (!m.from) return;
      rows.push({
        employment_id: e.employerId,
        start_date: m.from,
        end_date: m.to || m.from,
        name: "off_time",
      });
    });
    return rows;
  });

const EmploymentDatesModal = ({ show, onHide, employers = [], refetch = () => {} }) => {
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);

  const dropdownRef = useRef(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState(null);

  const { register, handleSubmit, reset, watch } = useForm({
    defaultValues: { start: "", end: "", missed_from: "", missed_to: "" },
  });

  const wStart = watch("start");
  const wEnd = watch("end");
  const wMissedFrom = watch("missed_from");

  const canAdd = selectedEmployer;

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const addEntry = handleSubmit((data) => {
    if (!selectedEmployer) return;
    const missed = data.missed_from ? [{ from: data.missed_from, to: data.missed_to }] : [];
    const entry = {
      id: `${selectedEmployer.id}-${Date.now()}`,
      employerId: selectedEmployer.id,
      employerName: selectedEmployer.employment_contact?.name,
      start: data.start,
      end: data.end,
      missed,
    };
    // Nothing to expand (no start/end/missed) — don't add an invisible entry.
    if (expandEmploymentDays(entry).length === 0) return;
    setEntries((prev) => [...prev, entry]);
    reset({ start: "", end: "", missed_from: "", missed_to: "" });
    setSelectedEmployer(null);
  });

  const expanded = entries.map((e) => ({ entry: e, days: expandEmploymentDays(e) }));
  const totalRows = expanded.reduce((n, x) => n + x.days.length, 0);

  const handleClose = () => {
    reset();
    setEntries([]);
    setSelectedEmployer(null);
    setDropdownOpen(false);
    onHide?.();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = toHistoryPayload(entries);
      if (payload.length === 0) return;
      const res = await api_without_cancellation.post(HISTORY_ENDPOINT, payload);
      if (res.status >= 200 && res.status < 300) {
        refetch();
        handleClose();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      dialogClassName="max-1050 modal-dialog-centered"
      contentClassName="custom-modal-new-provider"
      size="lg"
    >
      <div style={{ minHeight: "490px" }}>
        <Modal.Header
          style={{ borderBottom: "0px" }}
          className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
        >
          <Modal.Title
            className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
            style={{ fontSize: "14px", fontWeight: "600" }}
          >
            Employment Dates
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ minHeight: "500px", padding: "0px" }}>
          <div className="custom-tab">
            {/* Employer + Start/End */}
            <Row style={{ paddingLeft: "5px", paddingRight: "5px" }} className="mx-0">
              <Col md={12} className="p-0 p-t-5">
                <div className="d-flex mx-0 align-items-center custom-margin-bottom">
                  <div
                    ref={dropdownRef}
                    style={{
                      position: "relative",
                    }}
                    className="flex-g-1 height-25 p-l-0 pe-0 custom-select-new-provider"
                  >
                    <div
                      className="dropdown-button form-control p-0 p-l-5 rounded-0 height-25 d-flex align-items-center"
                      style={{ cursor: "pointer", color: "black" }}
                      onClick={() => setDropdownOpen((p) => !p)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setDropdownOpen((p) => !p);
                        }
                      }}
                    >
                      <span id="selectedOption">
                        {selectedEmployer
                          ? selectedEmployer.employment_contact?.name
                          : "Select Employer"}
                      </span>
                    </div>

                    {dropdownOpen && (
                      <div
                        className="dropdown-menu p-0 mt-0"
                        style={{
                          width: "100%",
                          border: "1px solid #ced4da",
                          maxHeight: "185px",
                          overflowY: "auto",
                          scrollbarWidth: "none",
                        }}
                      >
                        {employers.map((e) => (
                          <div
                            key={e.id}
                            className="dropdown-option d-flex align-items-center height-25 p-l-5"
                            style={{
                              cursor: "pointer",
                              fontWeight: "600",
                              color: "var(--primary)",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            onClick={() => {
                              setSelectedEmployer(e);
                              setDropdownOpen(false);
                            }}
                          >
                            {e.employment_contact?.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div
                    className="p-l-5 p-r-5"
                    style={{
                      width: "90px",
                      color: "var(--primary-25)",
                      fontWeight: "600",
                    }}
                  >
                    Start Date:
                  </div>
                  <div className="col-md-2 p-l-0 pe-0">
                    <input
                      type="date"
                      max={wEnd || undefined}
                      className="form-control height-25 p-0 p-l-5 rounded-0"
                      style={{ color: "black" }}
                      {...register("start")}
                    />
                  </div>
                  <div
                    className="p-l-5 p-r-5"
                    style={{
                      width: "90px",
                      color: "var(--primary-25)",
                      fontWeight: "600",
                    }}
                  >
                    End Date:
                  </div>
                  <div className="col-md-2 p-l-0 pe-0">
                    <input
                      type="date"
                      min={wStart || undefined}
                      className="form-control height-25 p-0 p-l-5 rounded-0"
                      style={{ color: "black" }}
                      {...register("end")}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            {/* Missed range — bounded to [start, end] */}
            <div
              style={{ paddingLeft: "5px", paddingRight: "5px" }}
              className="mx-0 w-100 d-flex align-items-center"
            >
              <div
                className=" p-r-5"
                style={{
                  width: "140px",
                  color: "var(--primary-25)",
                  fontWeight: "600",
                }}
              >
                Off Time Start Date:
              </div>
              <div className="flex-g-1 p-l-0 height-25 pe-0">
                <input
                  type="date"
                  min={wStart || undefined}
                  max={wEnd || undefined}
                  className="form-control height-25 p-0 p-l-5 rounded-0"
                  style={{ color: "black" }}
                  {...register("missed_from")}
                />
              </div>
              <div
                className="p-l-5 p-r-5"
                style={{
                  width: "140px",
                  color: "var(--primary-25)",
                  fontWeight: "600",
                }}
              >
                Off Time End Date:
              </div>
              <div className="flex-g-1 p-l-0 height-25 pe-0">
                <input
                  type="date"
                  min={wMissedFrom || wStart || undefined}
                  max={wEnd || undefined}
                  className="form-control height-25 p-0 p-l-5 rounded-0"
                  style={{ color: "black" }}
                  {...register("missed_to")}
                />
              </div>
            </div>

            <div className="row mx-0 m-t-5 align-items-center custom-margin-bottom">
              <div className="d-flex justify-content-end align-items-center height-25 flex-g-1 p-r-5">
                <button
                  type="button"
                  className="btn btn-primary border-no height-25 d-flex align-items-center"
                  onClick={addEntry}
                  disabled={!canAdd}
                  style={{ cursor: canAdd ? "pointer" : "not-allowed", gap: "5px" }}
                >
                  <span className="fw-bold ic-15 d-flex align-items-center">
                    <img src={addIconGold} alt="" />
                  </span>
                  Add Employer Dates
                </button>
              </div>
            </div>

            {/* Preview table */}
            <Row className="mx-0">
              <div
                style={{
                  height: "405px",
                  overflowY: "auto",
                  width: "100%",
                  scrollbarWidth: "none",
                }}
                className="invisible-scrollbar"
              >
                <table
                  className="table table-borderless table-striped table-treatment has-specialty-icon has-height-25"
                  style={{ width: "100%" }}
                >
                  <thead style={{ position: "sticky", top: "0" }}>
                    <tr id="tb-header">
                      <th
                        style={{ minWidth: "150px" }}
                        className="td-autosize text-center color-grey-2"
                      >
                        Employer
                      </th>
                      <th
                        // style={{ width: "110px" }}
                        className="td-autosize text-center color-grey-2"
                      >
                        Date
                      </th>
                      <th className="text-center color-grey-2">{/*  */}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expanded.map(({ entry, days }) =>
                      days.map((d) => (
                        <tr key={`${entry.id}-${d.date}`} className="height-25">
                          <td
                            className="td-autosize color-black p-l-5"
                            style={{ fontWeight: "600" }}
                          >
                            {entry.employerName}
                          </td>
                          <td className="color-black text-end p-l-5" style={{ fontWeight: "600" }}>
                            {formatDateForPanelDisplay(d.date)}
                          </td>
                          <td className="color-black p-l-5" style={{ fontWeight: "600" }}>
                            {d.label}
                          </td>
                        </tr>
                      ))
                    )}
                    {Array.from({ length: Math.max(0, MIN_ROWS - totalRows) }).map((_, i) => (
                      <tr key={`fake-${i}`} className="fake-rows-new-provider height-25">
                        <td colSpan={COLS} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Row>
          </div>
        </Modal.Body>

        <Modal.Footer
          className="p-0 mt-0 padding-outside-btn-new-provider"
          style={{ borderTop: "none" }}
        >
          <Button
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant={entries.length > 0 ? "success" : "secondary"}
            disabled={entries.length === 0 || saving}
            style={{ cursor: entries.length === 0 ? "not-allowed" : "pointer", minWidth: "108px" }}
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            onClick={handleSave}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default EmploymentDatesModal;
